import { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Icon } from '../../components/Icon.jsx';
import Modal from '../../components/Modal.jsx';
import { useT, useLang, t as translate } from '../../lib/i18n.jsx';
import { get, post, patch, del } from '../../lib/api.js';
import { useAsync } from '../../lib/hooks.js';
import { formatNumber } from '../../lib/format.js';
import { toast } from '../../lib/toast.jsx';
import { ConfirmDialog, DetailState, FormField, InlineError } from '../hosting/detailUi.jsx';

const EMAIL_RE = /^[^@\s]+@[^@\s]+\.[^@\s]+$/;

export default function EmailDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const t = useT();
  const { lang } = useLang();

  const { data: account, loading, error, reload } = useAsync(() => get(`/emails/accounts/${id}`), [id]);
  const [tab, setTab] = useState('overview');
  const [pwModal, setPwModal] = useState(false);

  if (loading || error || !account) {
    return (
      <DetailState
        lang={lang}
        loading={loading}
        error={error}
        backLabel={t('ed.back')}
        onBack={() => navigate('/e-mail')}
        notFoundTitle={t('emailDetail.notFound')}
        onRetry={reload}
      />
    );
  }

  return (
    <main className="page" data-screen-label={`Mailbox: ${account.email}`}>
      <button className="back-link" onClick={() => navigate('/e-mail')}>
        <Icon name="arrow-left" size={14} />
        <span style={{ marginLeft: 4 }}>{t('ed.back')}</span>
      </button>

      <div className="detail-head" style={{ flexWrap: 'wrap' }}>
        <div className="fav-lg"><Icon name="mail" size={20} /></div>
        <div style={{ flex: 1, minWidth: 0 }}>
          <h1 style={{ fontSize: 22, overflowWrap: 'anywhere' }}>{account.email}</h1>
          <div style={{ display: 'flex', gap: 8, marginTop: 6, alignItems: 'center', flexWrap: 'wrap' }}>
            <span className={'status-pill ' + (account.status === 'active' ? '' : 'is-warn')}>
              <span className="dot" />
              {statusLabel(account.status, lang)}
            </span>
          </div>
        </div>
        <button className="btn-ghost" onClick={() => setPwModal(true)}>
          {t('ed.changepwd')}
        </button>
      </div>

      {pwModal && <ChangePasswordModal lang={lang} account={account} onClose={() => setPwModal(false)} />}

      <div className="tabs" role="tablist">
        {[
          ['overview', 'ed.tab.overview'],
          ['forward', 'ed.tab.forward'],
          ['vacation', 'ed.tab.vacation'],
          ['aliases', 'ed.tab.aliases'],
          ['settings', 'ed.tab.settings'],
        ].map(([k, key]) => (
          <button key={k} role="tab" aria-selected={tab === k} aria-current={tab === k ? 'page' : undefined} onClick={() => setTab(k)}>
            {t(key)}
          </button>
        ))}
      </div>

      {tab === 'overview' && <OverviewTab account={account} lang={lang} />}
      {tab === 'forward' && <ForwardTab account={account} lang={lang} onSaved={reload} />}
      {tab === 'vacation' && <VacationTab account={account} lang={lang} />}
      {tab === 'aliases' && <AliasesTab account={account} lang={lang} />}
      {tab === 'settings' && <SettingsTab account={account} lang={lang} onSaved={reload} onDeleted={() => navigate('/e-mail')} />}
    </main>
  );
}

const STATUS_KEYS = {
  active: 'em.status.active',
  suspended: 'emailList.status.suspended',
  pending: 'emailList.status.pending',
  disabled: 'emailList.status.disabled',
};

function statusLabel(s, lang) {
  return STATUS_KEYS[s] ? translate(lang, STATUS_KEYS[s]) : s;
}

function Kv({ k, v, mono }) {
  return (
    <div className="kv">
      <span className="k">{k}</span>
      <span className={'v ' + (mono ? 'mono' : '')} style={{ overflowWrap: 'anywhere' }}>{v}</span>
    </div>
  );
}

function quotaLabel(mb, lang) {
  if (!mb) return '—';
  return mb >= 1024 ? `${formatNumber(mb / 1024, lang, { maximumFractionDigits: 1 })} GB` : `${formatNumber(mb, lang)} MB`;
}

// ─────────────────────────────────────────────────────────────
// Overview — mail client settings come from the domain's DNS instructions

function OverviewTab({ account, lang }) {
  const t = useT();
  const domain = account.domain || account.email.split('@')[1];
  const { data: dns } = useAsync(
    () => get(`/emails/domains/${account.email_domain_id}/dns-instructions`).catch(() => null),
    [account.email_domain_id],
  );
  const records = dns?.records || [];
  const hasCname = (n) => records.some((r) => r.type === 'CNAME' && r.name === n);
  const mx = records.find((r) => r.type === 'MX')?.value;
  const imapHost = hasCname('imap') ? `imap.${domain}` : mx;
  const smtpHost = hasCname('smtp') ? `smtp.${domain}` : mx;

  return (
    <div className="det-grid">
      <section className="card">
        <div className="card-h"><h3>{t('em.type.mailbox')}</h3></div>
        <div style={{ padding: '6px 22px 16px' }}>
          <Kv k={t('em.col.address')} v={account.email} mono />
          <Kv k={t('emailDetail.quota')} v={quotaLabel(account.quota_mb, lang)} />
          <Kv
            k={t('ed.tab.forward')}
            v={account.forward_to
              ? account.keep_copy
                ? t('emailDetail.forward.keepCopy', { address: account.forward_to })
                : account.forward_to
              : t('emailDetail.off')}
          />
          <Kv k={t('em.col.status')} v={statusLabel(account.status, lang)} />
        </div>
      </section>

      <section className="card">
        <div className="card-h"><h3>{t('emailDetail.client.title')}</h3></div>
        <div style={{ padding: '6px 22px 16px' }}>
          {imapHost ? (
            <>
              <Kv k="IMAP" v={`${imapHost}:993 (SSL/TLS)`} mono />
              <Kv k="SMTP" v={`${smtpHost}:465 (SSL/TLS)`} mono />
              <Kv k={t('ed.access.user')} v={account.email} mono />
            </>
          ) : (
            <div style={{ fontSize: 13, color: 'var(--ink-3)', padding: '8px 0' }}>
              {t('emailDetail.client.unavailable')}
            </div>
          )}
        </div>
      </section>
    </div>
  );
}

function ChangePasswordModal({ account, lang, onClose }) {
  const t = useT();
  const [pw, setPw] = useState('');
  const [pw2, setPw2] = useState('');
  const [err, setErr] = useState('');
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (pw.length < 12) {
      setErr(t('emailDetail.pw.min'));
      return;
    }
    if (pw !== pw2) {
      setErr(t('emailDetail.pw.mismatch'));
      return;
    }
    setBusy(true);
    setErr('');
    try {
      await patch(`/emails/accounts/${account.id}`, { password: pw });
      toast(t('tst.pwd_changed'));
      onClose();
    } catch (e2) {
      setErr(e2);
      setBusy(false);
    }
  };

  return (
    <Modal
      as="form"
      onSubmit={submit}
      onClose={onClose}
      maxWidth={480}
      title={t('ed.changepwd')}
      description={<span className="mono" style={{ fontSize: 12.5 }}>{account.email}</span>}
      footer={
        <>
          <button type="button" className="btn-ghost" onClick={onClose}>{t('g.cancel')}</button>
          <button type="submit" className="btn-primary" disabled={busy}>
            {busy ? t('emailDetail.working') : t('emailDetail.change')}
          </button>
        </>
      }
    >
      <div className="modal-body">
        <InlineError error={err} lang={lang} />
        <FormField label={t('mod.pwd.new')} hint={t('emailDetail.pw.min')}>
          <input type="password" autoComplete="new-password" required value={pw} onChange={(e) => setPw(e.target.value)} />
        </FormField>
        <FormField label={t('mod.pwd.confirm')}>
          <input type="password" autoComplete="new-password" required value={pw2} onChange={(e) => setPw2(e.target.value)} />
        </FormField>
      </div>
    </Modal>
  );
}

// ─────────────────────────────────────────────────────────────
// Forward — PATCH /emails/accounts/{id} { forward_to, keep_copy }

function ForwardTab({ account, lang, onSaved }) {
  const t = useT();
  const [on, setOn] = useState(!!account.forward_to);
  const [to, setTo] = useState(account.forward_to || '');
  const [keep, setKeep] = useState(account.keep_copy !== false);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState(null);
  const invalid = on && to.trim() !== '' && !EMAIL_RE.test(to.trim());

  const save = async (e) => {
    e.preventDefault();
    if (on && (!to.trim() || invalid)) return;
    setBusy(true);
    setErr(null);
    try {
      // forward_to null removes the forwarder (also on the mail server).
      await patch(`/emails/accounts/${account.id}`, on ? { forward_to: to.trim(), keep_copy: keep } : { forward_to: null });
      toast(t('emailDetail.forward.saved'));
      onSaved?.();
    } catch (e2) {
      setErr(e2);
    } finally {
      setBusy(false);
    }
  };

  return (
    <section className="card">
      <div className="card-h"><h3>{t('ed.tab.forward')}</h3></div>
      <form style={{ padding: '8px 22px 22px' }} onSubmit={save}>
        <p style={{ margin: '0 0 16px', fontSize: 13, color: 'var(--ink-3)', lineHeight: 1.5 }}>
          {t('emailDetail.forward.desc')}
        </p>
        <InlineError error={err} lang={lang} />
        <label style={{ display: 'inline-flex', alignItems: 'center', gap: 8, fontSize: 13, color: 'var(--ink-2)', marginBottom: 14 }}>
          <input type="checkbox" checked={on} onChange={() => setOn(!on)} />
          <span>{t('emailDetail.forward.on')}</span>
        </label>
        {on && (
          <FormField label={t('emailDetail.forward.to')} error={invalid ? t('emailDetail.invalidEmail') : ''}>
            <input type="email" value={to} onChange={(e) => setTo(e.target.value)} placeholder={t('emailDetail.forward.placeholder')} className="mono" required />
          </FormField>
        )}
        <label style={{ display: 'inline-flex', alignItems: 'center', gap: 8, fontSize: 13, color: 'var(--ink-2)' }}>
          <input type="checkbox" checked={keep} onChange={() => setKeep(!keep)} disabled={!on} />
          <span>{t('emailDetail.forward.keep')}</span>
        </label>
        <div style={{ marginTop: 16 }}>
          <button type="submit" className="btn-primary" disabled={busy || invalid}>
            {busy ? t('emailDetail.working') : t('g.save')}
          </button>
        </div>
      </form>
    </section>
  );
}

// ─────────────────────────────────────────────────────────────
// Vacation — GET/POST/DELETE /emails/accounts/{id}/autoresponder

function VacationTab({ account, lang }) {
  const t = useT();
  const { data, loading, error, reload } = useAsync(() => get(`/emails/accounts/${account.id}/autoresponder`), [account.id]);
  const [on, setOn] = useState(false);
  const [subject, setSubject] = useState('');
  const [message, setMessage] = useState('');
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState(null);

  useEffect(() => {
    if (!data) return;
    setOn(!!data.autoresponder_enabled);
    setSubject(data.autoresponder_subject || t('emailDetail.vacation.defaultSubject'));
    setMessage(data.autoresponder_message || '');
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data]);

  const save = async (e) => {
    e.preventDefault();
    setBusy(true);
    setErr(null);
    try {
      if (on) {
        await post(`/emails/accounts/${account.id}/autoresponder`, { subject: subject.trim(), message, enabled: true });
      } else if (data?.autoresponder_enabled) {
        await del(`/emails/accounts/${account.id}/autoresponder`);
      }
      toast(t('emailDetail.vacation.saved'));
      reload();
    } catch (e2) {
      setErr(e2);
    } finally {
      setBusy(false);
    }
  };

  return (
    <section className="card">
      <div className="card-h"><h3>{t('emailDetail.vacation.title')}</h3></div>
      <form style={{ padding: '8px 22px 22px' }} onSubmit={save}>
        <p style={{ margin: '0 0 16px', fontSize: 13, color: 'var(--ink-3)', lineHeight: 1.5 }}>
          {t('emailDetail.vacation.desc')}
        </p>
        {loading && <div style={{ fontSize: 13, color: 'var(--ink-3)' }}>{t('common.loading')}</div>}
        <InlineError error={error || err} lang={lang} />
        {!loading && !error && (
          <>
            <label style={{ display: 'inline-flex', alignItems: 'center', gap: 8, fontSize: 13, color: 'var(--ink-2)', marginBottom: 14 }}>
              <input type="checkbox" checked={on} onChange={() => setOn(!on)} />
              <span>{t('emailDetail.vacation.on')}</span>
            </label>
            <FormField label={t('ev.subject')}>
              <input value={subject} onChange={(e) => setSubject(e.target.value)} required={on} disabled={!on} />
            </FormField>
            <FormField label={t('ev.message')}>
              <textarea
                value={message}
                onChange={(e) => setMessage(e.target.value)}
                rows={8}
                required={on}
                disabled={!on}
                placeholder={t('emailDetail.vacation.placeholder')}
                style={{ resize: 'vertical', minHeight: 140, lineHeight: 1.5 }}
              />
            </FormField>
            <button type="submit" className="btn-primary" disabled={busy}>
              {busy ? t('emailDetail.working') : t('g.save')}
            </button>
          </>
        )}
      </form>
    </section>
  );
}

// ─────────────────────────────────────────────────────────────
// Aliases — GET /emails/aliases?account_id, POST /emails/aliases, DELETE /emails/aliases/{id}

function AliasesTab({ account, lang }) {
  const t = useT();
  const domain = account.domain || account.email.split('@')[1];
  const { data, loading, error, reload } = useAsync(() => get(`/emails/aliases?account_id=${account.id}`), [account.id]);
  const [addOpen, setAddOpen] = useState(false);
  const [toDelete, setToDelete] = useState(null);
  const aliases = data || [];

  return (
    <section className="card" style={{ padding: 0 }}>
      <div className="files-toolbar" style={{ flexWrap: 'wrap', gap: 10 }}>
        <div>
          <h3 style={{ margin: 0, fontSize: 14, fontWeight: 600 }}>{t('ea.title')}</h3>
          <div style={{ fontSize: 12, color: 'var(--ink-3)', marginTop: 2 }}>
            {t('emailDetail.aliases.desc')}
          </div>
        </div>
        <button className="btn-primary" onClick={() => setAddOpen(true)}>
          <Icon name="plus" size={14} stroke={2} />
          <span style={{ marginLeft: 6 }}>{t('ea.add')}</span>
        </button>
      </div>
      {loading && <div style={{ padding: '14px 22px', color: 'var(--ink-3)', fontSize: 13 }}>{t('common.loading')}</div>}
      {error && <div style={{ padding: '0 22px 14px' }}><InlineError error={error} lang={lang} /></div>}
      {!loading && !error && aliases.length === 0 && (
        <div className="empty-state">
          <div className="iccirc"><Icon name="user" size={22} /></div>
          <h4>{t('emailDetail.aliases.empty.title')}</h4>
          <p>{t('emailDetail.aliases.empty.body')}</p>
        </div>
      )}
      {aliases.map((a) => (
        <div className="file-row" style={{ gridTemplateColumns: 'minmax(0,1fr) auto' }} key={a.id}>
          <div className="file-row__name" style={{ minWidth: 0 }}>
            <span className="file-row__icon"><Icon name="user" size={14} /></span>
            <span className="mono" style={{ overflowWrap: 'anywhere' }}>{a.alias_email}</span>
          </div>
          <div style={{ textAlign: 'right' }}>
            <button className="icon-btn" onClick={() => setToDelete(a)} title={t('emailDetail.delete')} aria-label={t('emailDetail.aliases.deleteAria', { address: a.alias_email })}>
              <Icon name="trash" size={14} />
            </button>
          </div>
        </div>
      ))}

      {addOpen && (
        <AddAliasModal
          account={account}
          domain={domain}
          lang={lang}
          onClose={() => setAddOpen(false)}
          onCreated={() => {
            setAddOpen(false);
            toast(t('emailDetail.aliases.added'));
            reload();
          }}
        />
      )}
      {toDelete && (
        <ConfirmDialog
          lang={lang}
          title={t('emailDetail.aliases.confirmTitle')}
          body={t('emailDetail.aliases.confirmBody', { address: toDelete.alias_email })}
          cta={t('emailDetail.delete')}
          onClose={() => setToDelete(null)}
          onConfirm={async () => {
            await del(`/emails/aliases/${toDelete.id}`);
            setToDelete(null);
            toast(t('emailDetail.aliases.deleted'));
            reload();
          }}
        />
      )}
    </section>
  );
}

function AddAliasModal({ account, domain, lang, onClose, onCreated }) {
  const t = useT();
  const [deliversBefore, deliversAfter = ''] = t('emailDetail.aliasModal.deliversTo').split('{address}');
  const [local, setLocal] = useState('');
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState(null);
  const valid = /^[a-z0-9._+-]+$/i.test(local.trim());

  const submit = async (e) => {
    e.preventDefault();
    if (!valid) return;
    setBusy(true);
    setErr(null);
    try {
      await post('/emails/aliases', { email_account_id: account.id, alias_username: local.trim().toLowerCase() });
      onCreated();
    } catch (e2) {
      setErr(e2);
      setBusy(false);
    }
  };

  return (
    <Modal
      as="form"
      onSubmit={submit}
      onClose={onClose}
      maxWidth={460}
      title={t('ea.add')}
      description={<>{deliversBefore}<span className="mono">{account.email}</span>{deliversAfter}</>}
      footer={
        <>
          <button type="button" className="btn-ghost" onClick={onClose}>{t('g.cancel')}</button>
          <button type="submit" className="btn-primary" disabled={busy || !valid}>
            {busy ? t('emailDetail.working') : t('emailDetail.create')}
          </button>
        </>
      }
    >
      <div className="modal-body">
        <InlineError error={err} lang={lang} />
        <FormField
          label={t('emailDetail.aliasModal.label')}
          hint={`${local.trim() || t('emailDetail.aliasModal.name')}@${domain}`}
          error={local && !valid ? t('emailDetail.aliasModal.invalid') : ''}
        >
          <input required value={local} onChange={(e) => setLocal(e.target.value)} placeholder="info" className="mono" autoComplete="off" />
        </FormField>
      </div>
    </Modal>
  );
}

// ─────────────────────────────────────────────────────────────
// Settings — quota (PATCH quota_mb), spam settings, delete

const QUOTAS_MB = [1024, 5120, 10240, 25600, 51200];

function SettingsTab({ account, lang, onSaved, onDeleted }) {
  const t = useT();
  const [quota, setQuota] = useState(account.quota_mb);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState(null);
  const [confirmDelete, setConfirmDelete] = useState(false);
  // Mailboxes share the domain's storage: offer only sizes that fit in what is left.
  const { data: room } = useAsync(async () => {
    const [domains, accounts] = await Promise.all([get('/emails/domains'), get('/emails/accounts')]);
    const domain = (domains || []).find((d) => d.id === account.email_domain_id);
    if (!domain) return null;
    const others = (accounts || [])
      .filter((a) => a.email_domain_id === domain.id && a.id !== account.id)
      .reduce((sum, a) => sum + (a.quota_mb || 0), 0);
    return domain.disk_quota_mb - others;
  }, [account.id, account.email_domain_id]);
  const options = Array.from(new Set([...QUOTAS_MB, account.quota_mb, ...(room > 0 ? [room] : [])]))
    .filter((v) => v && (room == null || v <= room || v === account.quota_mb))
    .sort((a, b) => a - b);

  const saveQuota = async () => {
    setBusy(true);
    setErr(null);
    try {
      await patch(`/emails/accounts/${account.id}`, { quota_mb: quota });
      toast(t('emailDetail.quota.saved'));
      onSaved?.();
    } catch (e) {
      setErr(e);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="det-grid">
      <section className="card">
        <div className="card-h"><h3>{t('emailDetail.quota.title')}</h3></div>
        <div style={{ padding: '6px 22px 18px' }}>
          <p style={{ margin: '0 0 14px', fontSize: 13, color: 'var(--ink-3)', lineHeight: 1.5 }}>
            {t('emailDetail.quota.desc')}
          </p>
          <InlineError error={err} lang={lang} />
          <div className="seg" role="group" aria-label={t('emailDetail.quota.title')} style={{ flexWrap: 'wrap' }}>
            {options.map((v) => (
              <button key={v} type="button" aria-pressed={quota === v} onClick={() => setQuota(v)}>{quotaLabel(v, lang)}</button>
            ))}
          </div>
          <div style={{ marginTop: 14 }}>
            <button className="btn-primary" disabled={busy || quota === account.quota_mb} onClick={saveQuota}>
              {busy ? t('emailDetail.working') : t('g.save')}
            </button>
          </div>
        </div>
      </section>

      <SpamCard account={account} lang={lang} />

      <section className="card danger-card" style={{ gridColumn: '1 / -1' }}>
        <div className="card-h"><h3>{t('emailDetail.danger.title')}</h3></div>
        <div style={{ padding: '6px 22px 18px', display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 16, flexWrap: 'wrap' }}>
          <p style={{ margin: 0, fontSize: 13, color: 'var(--ink-3)', lineHeight: 1.5, maxWidth: 520 }}>
            {t('emailDetail.danger.desc')}
          </p>
          <button className="btn-danger" onClick={() => setConfirmDelete(true)}>
            {t('es.danger.title')}
          </button>
        </div>
      </section>

      {confirmDelete && (
        <ConfirmDialog
          lang={lang}
          title={t('mod.delmb.title')}
          body={t('emailDetail.danger.confirmBody', { address: account.email })}
          cta={t('emailDetail.delete')}
          onClose={() => setConfirmDelete(false)}
          onConfirm={async () => {
            await del(`/emails/accounts/${account.id}`);
            toast(t('emailDetail.deleted'));
            onDeleted();
          }}
        />
      )}
    </div>
  );
}

// Spam threshold: lower score = stricter filter.
const SPAM_LEVELS = [
  ['strict', 3],
  ['balanced', 5],
  ['lenient', 8],
];

function SpamCard({ account, lang }) {
  const t = useT();
  const { data, loading, error, reload } = useAsync(() => get(`/emails/accounts/${account.id}/spam-settings`), [account.id]);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState(null);

  const current = data
    ? !data.spam_filter_enabled
      ? 'off'
      : SPAM_LEVELS.reduce((best, [k, v]) => (Math.abs(v - data.spam_score_threshold) < Math.abs(best[1] - data.spam_score_threshold) ? [k, v] : best), SPAM_LEVELS[1])[0]
    : null;

  const choose = async (level) => {
    setBusy(true);
    setErr(null);
    try {
      const body = level === 'off'
        ? { spam_filter_enabled: false }
        : { spam_filter_enabled: true, spam_score_threshold: SPAM_LEVELS.find(([k]) => k === level)[1] };
      await patch(`/emails/accounts/${account.id}/spam-settings`, body);
      toast(t('emailDetail.spam.saved'));
      reload();
    } catch (e) {
      setErr(e);
    } finally {
      setBusy(false);
    }
  };

  const labels = {
    strict: t('es.spam.strict'),
    balanced: t('es.spam.balanced'),
    lenient: t('emailDetail.spam.lenient'),
    off: t('emailDetail.off'),
  };

  return (
    <section className="card">
      <div className="card-h"><h3>{t('es.spam.title')}</h3></div>
      <div style={{ padding: '6px 22px 18px' }}>
        <p style={{ margin: '0 0 14px', fontSize: 13, color: 'var(--ink-3)', lineHeight: 1.5 }}>
          {t('emailDetail.spam.desc')}
        </p>
        {loading && <div style={{ fontSize: 13, color: 'var(--ink-3)' }}>{t('common.loading')}</div>}
        <InlineError error={error || err} lang={lang} />
        {data && (
          <div className="seg" role="group" aria-label={t('es.spam.title')} style={{ flexWrap: 'wrap' }}>
            {['strict', 'balanced', 'lenient', 'off'].map((k) => (
              <button key={k} type="button" aria-pressed={current === k} disabled={busy} onClick={() => current !== k && choose(k)}>
                {labels[k]}
              </button>
            ))}
          </div>
        )}
      </div>
    </section>
  );
}
